//*Libraries imports
import { Controller, useFormContext } from "react-hook-form";
import { CreditCard, HelpCircle } from "lucide-react";

//*Components Imports
import { CardBrandBadge } from "@/components/ui/CardBrandBadge";

//*Types Imports
import { CreditCardFormDTO } from "@/schemas/asaas/payment";

//*Utils Imports
import {
  detectCardBrand,
  formatCardNumber,
  normalizeHolderName,
} from "@/utils/cardUtils";

export function CreditCardDataSection() {
  const {
    control,
    register,
    watch,
    formState: { errors },
  } = useFormContext<CreditCardFormDTO>();

  const cardNumber = watch("creditCard.number");
  const cardBrand = detectCardBrand(cardNumber || "");

  return (
    <div className="bg-[#FCFCFC] rounded-[18px] p-5 border border-[#EDF2F6] flex flex-col gap-5">
      <div className="flex items-center gap-2 mb-1">
        <CreditCard className="size-5 text-claroprimaria-01" />
        <h3 className="font-bold text-clarotexto-201">Dados do Cartão</h3>
      </div>

      <div className="flex flex-col gap-1.5">
        <label
          className={`text-xs font-medium ${errors.creditCard?.number ? "text-red-500" : "text-gray-500"}`}
        >
          Número do cartão <span className="text-red-500">*</span>
        </label>
        <Controller
          name="creditCard.number"
          control={control}
          render={({ field }) => (
            <div
              className={`relative flex items-center border rounded-xl h-[48px] bg-clarofundo-01 transition-colors focus-within:bg-white focus-within:border-claroprimaria-01 ${errors.creditCard?.number ? "border-red-300 bg-red-50 focus-within:border-red-500" : "border-transparent"}`}
            >
              <input
                {...field}
                type="tel"
                onChange={(e) =>
                  field.onChange(formatCardNumber(e.target.value))
                }
                placeholder="0000 0000 0000 0000"
                className="w-full h-full px-4 bg-transparent outline-none text-clarotextos-01 placeholder:text-clarotextos-03"
              />
              <div className="absolute right-4">
                {cardBrand ? (
                  <CardBrandBadge brand={cardBrand} />
                ) : (
                  <CreditCard className="size-5 text-gray-400" />
                )}
              </div>
            </div>
          )}
        />
        {errors.creditCard?.number && (
          <span className="text-xs text-red-500">
            {errors.creditCard.number.message}
          </span>
        )}
      </div>

      <div className="flex gap-4">
        <div className="flex-1 flex flex-col gap-1.5">
          <label
            className={`text-xs font-medium ${errors.creditCard?.expiryMonth || errors.creditCard?.expiryYear ? "text-red-500" : "text-gray-500"}`}
          >
            Validade (MM/AA) <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-2">
            <Controller
              name="creditCard.expiryMonth"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type="tel"
                  maxLength={2}
                  placeholder="MM"
                  className={`w-1/2 border rounded-xl h-[48px] px-4 bg-clarofundo-01 transition-colors focus:bg-white focus:border-claroprimaria-01 outline-none ${errors.creditCard?.expiryMonth ? "border-red-300 bg-red-50 focus:border-red-500" : "border-transparent"}`}
                />
              )}
            />
            <Controller
              name="creditCard.expiryYear"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type="tel"
                  maxLength={2}
                  placeholder="AA"
                  className={`w-1/2 border rounded-xl h-[48px] px-4 bg-clarofundo-01 transition-colors focus:bg-white focus:border-claroprimaria-01 outline-none ${errors.creditCard?.expiryYear ? "border-red-300 bg-red-50 focus:border-red-500" : "border-transparent"}`}
                />
              )}
            />
          </div>
          {(errors.creditCard?.expiryMonth ||
            errors.creditCard?.expiryYear) && (
            <span className="text-xs text-red-500">
              {errors.creditCard.expiryMonth?.message ||
                errors.creditCard.expiryYear?.message}
            </span>
          )}
        </div>

        {/* CVV */}
        <div className="w-[100px] flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <label
              className={`text-xs font-medium ${errors.creditCard?.ccv ? "text-red-500" : "text-gray-500"}`}
            >
              CVV <span className="text-red-500">*</span>
            </label>
            <div className="group relative">
              <HelpCircle className="size-3 text-gray-400 cursor-help" />
              <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-32 bg-gray-800 text-white text-[10px] p-2 rounded z-10">
                Código de 3 ou 4 dígitos no verso do cartão
              </div>
            </div>
          </div>
          <Controller
            name="creditCard.ccv"
            control={control}
            render={({ field }) => (
              <div
                className={`flex items-center border rounded-xl h-[48px] bg-clarofundo-01 transition-colors focus-within:bg-white focus-within:border-claroprimaria-01 ${errors.creditCard?.ccv ? "border-red-300 bg-red-50 focus-within:border-red-500" : "border-transparent"}`}
              >
                <input
                  {...field}
                  type="tel"
                  onChange={(e) =>
                    field.onChange(
                      e.target.value.replace(/\D/g, "").slice(0, 4),
                    )
                  }
                  placeholder="123"
                  className="w-full h-full px-4 bg-transparent outline-none text-clarotextos-01 placeholder:text-clarotextos-03"
                />
              </div>
            )}
          />
          {errors.creditCard?.ccv && (
            <span className="text-xs text-red-500">
              {errors.creditCard.ccv.message}
            </span>
          )}
        </div>
      </div>

      {/* Nome do Titular */}
      <div className="flex flex-col gap-1.5">
        <label
          className={`text-xs font-medium ${errors.creditCard?.holderName ? "text-red-500" : "text-gray-500"}`}
        >
          Nome do titular <span className="text-red-500">*</span>
        </label>
        <Controller
          name="creditCard.holderName"
          control={control}
          render={({ field }) => (
            <div
              className={`flex items-center border rounded-xl h-[48px] bg-clarofundo-01 transition-colors focus-within:bg-white focus-within:border-claroprimaria-01 ${errors.creditCard?.holderName ? "border-red-300 bg-red-50 focus-within:border-red-500" : "border-transparent"}`}
            >
              <input
                {...field}
                type="text"
                onChange={(e) =>
                  field.onChange(normalizeHolderName(e.target.value))
                }
                placeholder="Nome impresso no cartão"
                className="w-full h-full px-4 bg-transparent outline-none text-clarotextos-01 placeholder:text-clarotextos-03"
              />
            </div>
          )}
        />
        {errors.creditCard?.holderName && (
          <span className="text-xs text-red-500">
            {errors.creditCard.holderName.message}
          </span>
        )}
      </div>
    </div>
  );
}
